import { useCallback, useRef } from 'react';

/**
 * Hook to produce an onFocus handler for react-nice-dates inputs that
 * triggers a window resize (after the current tick) the first time a picker
 * input receives focus while the calendar popover is (about to be) opened.
 * This forces the library to recompute cellHeight based on actual width,
 * avoiding the "flattened" calendar caused by initial zero-width measurement.
 */
export function useNiceDatesRecalcOnFocus() {
    const firedRef = useRef(false);
    return useCallback((orig?: () => void) => {
        if (orig) orig();
        if (!firedRef.current) {
            firedRef.current = true;
            // Defer so the popover has mounted and dimensions are available.
            setTimeout(() => {
                window.dispatchEvent(new Event('resize'));
            }, 0);
        }
    }, []);
}

/**
 * Convenience wrapper to adapt to render-prop objects:
 *   const onStartFocus = focusHandler(startDateInputProps.onFocus)
 */
export function useNiceDatesFocusAdapter() {
    const handler = useNiceDatesRecalcOnFocus();
    return (orig?: () => void) => () => handler(orig);
}

export default useNiceDatesRecalcOnFocus;
